import * as React from "react";
import {
    RiChat1Line,
    RiCloseLargeLine,
    RiGlobalLine,
    RiShoppingCart2Line,
    RiUserLine
} from "@remixicon/react";
import {motion} from "motion/react";
import {Link} from "~/components/ui/link";
import {FleetSubSection} from "~/components/header/collapsed-header/subnavigation/FleetSubSection";
import {ProductsSubSection} from "~/components/header/collapsed-header/subnavigation/ProductsSubSection";
import {PersonalUseSubSection} from "~/components/header/collapsed-header/subnavigation/PersonalUseSubSection";
import {ResourcesSubSection} from "~/components/header/collapsed-header/subnavigation/ResourcesSubSection";
import {Badge} from "~/components/ui/badge";
import {Accordion, AccordionItem, AccordionTrigger, AccordionContent} from "~/components/ui/accordion";
import {useTranslation} from "react-i18next";
import {IconButton} from "~/components/ui/icon-button";
import { useToggleEmbeddedCart } from "~/lib/use-embedded-cart";
import wordmarkBlue from "~/assets/logos/bouncie-wordmark-blue.svg";
import fleetSolutionsWhite from "~/assets/logos/bouncie-fleet-solutions-white.svg";
import { useIsFleetHeader } from "~/components/header/header-mode";
import {WEB_APP_URL} from "~/lib/external-links";

export function NavSection({ value, label, children }: { value: string; label: string; children: React.ReactNode }) {
    return (
        <AccordionItem value={value} className="border-border/50 fleet:border-nav-divider fleet:bg-transparent border-b">
            <AccordionTrigger data-sid={`nav-collapsed-trigger:${value}`} className="py-3 pr-4 pl-6">
                {label}
            </AccordionTrigger>
            <AccordionContent className="p-0">
                {children}
            </AccordionContent>
        </AccordionItem>
    );
}

export function NavItem({
                     label, to, badge, onClick, external,
                 }: {
    label: string; to: string; badge?: string; onClick: (e: React.MouseEvent<HTMLAnchorElement>) => void; external?: boolean;
}) {
    return (
        <Link data-sid={`nav-collapsed-link:${to}`}
            to={to}
            onClick={onClick}
            {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
            className="text-foreground hover:bg-nav-item-hover flex h-12 items-center gap-3 px-10 text-base font-medium transition-colors"
        >
            {label}
            {badge && <Badge variant="default" className="bg-figma-grey-mist text-figma-grey-onyx rounded-full">{badge}</Badge>}
        </Link>
    );
}

// Not currently rendered in the mobile nav — kept for when a login flow is wired up.
export function LoginRow({close}: {close: () => void}) {
    const { t } = useTranslation("common");
    return (
        <Link
            data-sid="mobile-nav-web-app"
            to={WEB_APP_URL}
            onClick={close}
            className="bg-card-fill-grey border-border/50 flex items-center justify-between gap-3 border-t px-4 py-3"
        >
            <div className="flex items-center gap-3">
                <div className="bg-bouncie-100 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
                    <RiUserLine className="text-text-heading h-5 w-5" />
                </div>
                <span className="font-display text-text-heading text-base font-bold">{t("nav.login")}</span>
            </div>
        </Link>
    );
}

export const OverlayContent = ({close, onOpenLocaleDialog}: {close: () => void; onOpenLocaleDialog: () => void}) => {
    const { t } = useTranslation("common");
    const toggleCart = useToggleEmbeddedCart();
    const fleet = useIsFleetHeader();

    const overlayCart = (
        <IconButton
            icon={RiShoppingCart2Line}
            variant="ghost"
            aria-label={t("cart")}
            onClick={() => {
                close();
                toggleCart();
            }}
        />
    );

    const overlayLogo = (
        <div className="flex shrink-0 items-center">
            {fleet ? (
                <Link to="/fleet" aria-label={t("brand.fleet_solutions")}>
                    <img src={fleetSolutionsWhite} alt={t("brand.fleet_solutions")} className="h-6 w-auto" />
                </Link>
            ) : (
                <Link to="/" aria-label="Bouncie">
                    <img src={wordmarkBlue} alt="Bouncie" className="h-[23px] w-auto" />
                </Link>
            )}
        </div>
    );


    return (
        <>
            <motion.div
                initial={{opacity: 0}}
                animate={{opacity: 1, transition: {duration: 0.18, delay: 0.18}}}
                exit={{opacity: 0, transition: {duration: 0.08}}}
                className={"flex h-24 shrink-0 items-center px-4"}
            >
                <div className="flex flex-1 items-center">
                    <IconButton
                        icon={RiCloseLargeLine}
                        onClick={close}
                        aria-label={t("nav.close_menu")}
                        variant="ghost"
                        iconClassName={"h-4 w-4"}
                        className="text-primary fleet:text-text-heading hover:bg-current/10 hover:text-current"
                    />
                </div>
                {overlayLogo}
                <div className="flex flex-1 items-center justify-end">
                    {overlayCart}
                </div>
            </motion.div>

            <motion.div
                initial={{opacity: 0}}
                animate={{opacity: 1, transition: {duration: 0.18, delay: 0.26}}}
                exit={{opacity: 0, transition: {duration: 0.08}}}
                className="flex flex-1 flex-col overflow-y-auto"
            >
                <Accordion type="multiple" defaultValue={["products"]}>
                    <ProductsSubSection close={close} />
                    <PersonalUseSubSection close={close}/>
                    <FleetSubSection close={close}/>
                    <ResourcesSubSection close={close}/>
                </Accordion>
                <div className="mt-auto">
                    <div className="border-border/50 fleet:border-nav-divider flex items-center justify-between border-t px-4 py-3">
                        {/* No chat provider is wired up yet — visual placeholder until one is integrated. */}
                        <button data-sid="nav-collapsed-chat"
                            type="button"
                            className="text-text-heading hover:bg-nav-item-hover flex items-center gap-3 rounded-lg px-2 py-2"
                        >
                            <div className="bg-nav-icon-chip-bg flex items-center justify-center rounded-md p-2">
                                <RiChat1Line className="fleet:text-icon-primary h-6 w-6" />
                            </div>
                            <span className="font-display text-base font-bold">{t("nav.chat")}</span>
                        </button>
                        <IconButton
                            icon={RiGlobalLine}
                            variant="ghost"
                            className="text-text-heading hover:bg-nav-item-hover hover:text-text-heading"
                            aria-label={t("nav.language")}
                            onClick={onOpenLocaleDialog}
                        />
                    </div>
                </div>
            </motion.div>
        </>
    )
};
